import React, { useState, useEffect } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar.tsx";
import OrganizationSiteFilter from "../components/OrganizationSiteFilter.tsx";
import { Site, Organization } from "../types/index.ts";
import {
  getMyOrganizations,
  getOrganizationSiteTree,
  Site as ApiSite,
} from "../services/organization.ts";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../store/index.ts";
import { fetchIQWatchDevices } from "../store/dashboardSlice.ts";

// 适配器函数：将API返回的Site转换为组件需要的Site
const convertApiSiteToSite = (apiSite: ApiSite, orgId: string): Site => {
  return {
    id: apiSite.id,
    name: apiSite.name,
    type: apiSite.type as "region" | "building" | "location",
    org_id: orgId,
    parentId: apiSite.parent_id,
    children: apiSite.children
      ? apiSite.children.map((child) => convertApiSiteToSite(child, orgId))
      : undefined,
    deviceCount:
      (apiSite.edge_devices ? apiSite.edge_devices.length : 0) +
      (apiSite.iqwatch_devices ? apiSite.iqwatch_devices.length : 0),
    createdAt: apiSite.created_at,
    updatedAt: apiSite.updated_at,
    detail: apiSite.detail,
    edge_devices: apiSite.edge_devices?.map((device) => ({
      ...device,
      timezone: "UTC", // Default timezone since organization tree API doesn't include this
    })),
    iqwatch_devices: apiSite.iqwatch_devices?.map((device) => ({
      ...device,
      timezone: "UTC", // Default timezone since organization tree API doesn't include this
    })),
  };
};

function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const getActiveTabFromPath = (path: string) => {
    const pathSegment = path.split("/")[1] || "dashboard";
    // Special case for profile page
    if (pathSegment === "profile") {
      return "profile";
    }
    return pathSegment;
  };

  const [activeTab, setActiveTab] = useState(
    getActiveTabFromPath(location.pathname)
  );
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization | null>(null);
  const [isAllSitesSelected, setIsAllSitesSelected] = useState(false);
  const [loading, setLoading] = useState(true);

  // 加载组织数据
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        setLoading(true);
        const orgs = await getMyOrganizations();

        // 为每个组织获取站点树数据
        const orgsWithSites = await Promise.all(
          orgs.map(async (org) => {
            try {
              const siteTreeData = await getOrganizationSiteTree(org.id);
              return {
                id: org.id,
                name: org.name,
                code: org.code,
                description: org.description,
                sites: siteTreeData.sites.map((site) =>
                  convertApiSiteToSite(site, org.id)
                ),
                deviceCount: siteTreeData.total_edge_devices,
              };
            } catch (error) {
              console.error(
                `Failed to load sites for organization ${org.id}:`,
                error
              );
              return {
                id: org.id,
                name: org.name,
                code: org.code,
                description: org.description,
                sites: [],
                deviceCount: 0,
              };
            }
          })
        );

        setOrganizations(orgsWithSites);
      } catch (error) {
        console.error("Failed to load organizations:", error);
        setOrganizations([]);
      } finally {
        setLoading(false);
      }
    };

    loadOrganizations();
  }, []);

  // 在DashboardLayout中添加预加载逻辑
  useEffect(() => {
    const preloadData = async () => {
      if (organizations.length > 0) {
        // 预加载第一个组织的iQWatch设备数据
        const firstOrg = organizations[0];
        if (firstOrg.sites.length > 0) {
          const firstSite = firstOrg.sites[0];
          dispatch(
            fetchIQWatchDevices({
              siteId: firstSite.id,
              orgId: firstOrg.id,
            })
          );
        }
      }
    };

    preloadData();
  }, [organizations, dispatch]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    navigate(`/${tab}`);
  };

  // Add handler for organization selection
  const handleOrganizationSelect = (org: Organization | null) => {
    setSelectedOrganization(org);
    setIsAllSitesSelected(false); // Reset all sites selection when org changes

    // When organization changes, clear site selection unless it belongs to the new org
    if (selectedSite && org) {
      const siteInOrg = org.sites.some((site) => {
        const checkSiteTree = (s: Site): boolean => {
          if (s.id === selectedSite.id) return true;
          return s.children?.some(checkSiteTree) || false;
        };
        return checkSiteTree(site);
      });

      if (!siteInOrg) {
        setSelectedSite(null);
      }
    } else if (!org) {
      // If no organization selected, clear site selection
      setSelectedSite(null);
    }
  };

  // Add handler for site selection
  const handleSiteSelect = (site: Site | null) => {
    setSelectedSite(site);
    setIsAllSitesSelected(false); // Reset all sites selection when specific site is selected

    // When a specific site is selected, we need to find and set its organization
    // to maintain the organization context for the dropdown
    if (site) {
      // Find the organization that contains this site
      const containingOrg = organizations.find((org) => {
        const checkSiteTree = (sites: Site[]): boolean => {
          return sites.some((s) => {
            if (s.id === site.id) return true;
            return s.children ? checkSiteTree(s.children) : false;
          });
        };
        return checkSiteTree(org.sites);
      });

      if (containingOrg && containingOrg.id !== selectedOrganization?.id) {
        setSelectedOrganization(containingOrg);
      }
    }
    // Note: We don't clear the organization when site is null,
    // as the user might want to switch to "All Sites" mode
  };

  // Add handler for "All Sites" selection
  const handleAllSitesSelect = () => {
    setSelectedSite(null);
    setIsAllSitesSelected(true);
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-50">
        <Sidebar activeTab={activeTab} onTabChange={handleTabChange} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading organizations...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={handleTabChange} />
      <div className="flex-1 flex flex-col overflow-auto">
        {activeTab === "dashboard" && (
          <div className="bg-white border-b border-gray-200 p-4">
            <OrganizationSiteFilter
              organizations={organizations}
              selectedOrganization={selectedOrganization}
              selectedSite={selectedSite}
              onOrganizationSelect={handleOrganizationSelect}
              onSiteSelect={handleSiteSelect}
              showAllSitesOption={true}
              isAllSitesSelected={isAllSitesSelected}
              onAllSitesSelect={handleAllSitesSelect}
            />
          </div>
        )}
        <div className="flex-1 overflow-x-auto">
          {/* Pass site context to all dashboard-related pages */}
          <Outlet
            context={{
              selectedSite,
              selectedOrganization,
              organizations,
              isAllSitesSelected, // Add this line
            }}
          />
        </div>
      </div>
    </div>
  );
}

export default DashboardLayout;
